import { t } from "@/lib/i18n/interpolate";
import {
  type FormulaError,
  type FormulaSegment,
  formulaSegments,
  type ParsedFormula,
} from "@/lib/tool-kit/chemistry/formula";

/**
 * Pieces shared by the chemistry-quantity widgets: a formula drawn with real subscripts
 * and superscripts, the messages for a formula that will not parse, and the working-box
 * class every calculator shows its arithmetic in.
 */

/** English messages for each way a formula can fail to parse. Widgets spread this in. */
export const FORMULA_ERROR_STRINGS = {
  empty: "Type a chemical formula, such as H2O or Ca(OH)2.",
  unexpected:
    "“{token}” cannot appear in a formula. Use element symbols, numbers, brackets, and · or * for a hydrate.",
  unknownElement:
    "“{token}” is not an element symbol. Symbols start with a capital letter: Co is cobalt, CO is carbon monoxide.",
  unbalanced: "The bracket “{token}” has no partner. Close every ( [ and {.",
  zeroCount:
    "A count of {token} is not allowed. Subscripts and coefficients start at 1.",
  emptyGroup: "The brackets {token} are empty.",
  badCharge: "Put a formula before the charge, such as Fe^3+ or SO4^2-.",
  tooLarge:
    "That is too large to read: counts up to 9999 and formulas up to 200 characters.",
} as const;

export type FormulaErrorStrings = Record<FormulaError["code"], string>;

export function formulaErrorMessage(
  messages: FormulaErrorStrings,
  error: FormulaError
): string {
  return t(messages[error.code], { token: error.token });
}

/** The working box: arithmetic in monospace, on a quiet background. */
export const WORKING_BOX =
  "space-y-1 rounded-lg border border-border bg-muted/30 p-4 font-mono text-muted-foreground text-sm leading-relaxed";

function Segments({ segments }: { segments: readonly FormulaSegment[] }) {
  return (
    <>
      {segments.map((segment, index) => {
        if (segment.style === "sub") {
          // biome-ignore lint/suspicious/noArrayIndexKey: positional runs of one formula
          return <sub key={index}>{segment.text}</sub>;
        }
        if (segment.style === "sup") {
          // biome-ignore lint/suspicious/noArrayIndexKey: positional runs of one formula
          return <sup key={index}>{segment.text}</sup>;
        }
        // biome-ignore lint/suspicious/noArrayIndexKey: positional runs of one formula
        return <span key={index}>{segment.text}</span>;
      })}
    </>
  );
}

/** A formula with subscripts and superscripts: Ca(OH)₂, SO₄²⁻. */
export function FormulaView({ formula }: { formula: ParsedFormula }) {
  return <Segments segments={formulaSegments(formula)} />;
}

/** Plain element symbol with a subscript count, for table cells and working lines. */
export function SymbolCount({
  symbol,
  count,
}: {
  symbol: string;
  count: string;
}) {
  return (
    <>
      {symbol}
      {count === "1" ? null : <sub>{count}</sub>}
    </>
  );
}
